import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check, Copy, Home, Building2, Minus, Plus, ArrowLeft, ArrowRight, Phone, Zap, CalendarCheck2, Star, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { RATES, TIME_SLOTS, calcTotal, BUSINESS, FREQUENCIES, PLAN_LENGTHS, frequencyLabel, monthlyEstimate, visitsFor, planLabel, ADDONS, EMPTY_ADDONS, calcAddons, addonCount } from "../lib/constants";
import { createBooking } from "../lib/api";

const STEPS = ["Service", "Time", "Details", "Pay with Zelle"];
const inputCls =
  "w-full rounded-xl bg-ink/80 border border-white/15 px-4 py-3.5 text-sm text-white placeholder:text-mist/60 outline-none focus:border-crystal focus:ring-1 focus:ring-crystal/50 transition-colors duration-200";

function CopyRow({ label, value, testid }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(value).catch(() => {});
    setCopied(true);
    toast.success(`${label} copied`);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="flex items-center justify-between gap-3 rounded-xl border border-white/15 bg-ink/70 px-4 py-3.5">
      <div className="min-w-0">
        <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-mist">{label}</div>
        <div className="font-mono text-sm text-white truncate" data-testid={testid}>{value}</div>
      </div>
      <button
        data-testid={`${testid}-copy`}
        onClick={copy}
        className="shrink-0 rounded-lg border border-white/15 p-2.5 text-mist hover:text-crystal hover:border-crystal transition-colors duration-200"
        aria-label={`Copy ${label}`}
      >
        {copied ? <Check size={14} className="text-crystal" /> : <Copy size={14} />}
      </button>
    </div>
  );
}

function AddonRow({ addon, qty, onSet }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-ink/50 px-4 py-3" data-testid={`addon-row-${addon.key}`}>
      <div className="min-w-0">
        <div className="text-sm text-white leading-tight">{addon.label}</div>
        <div className="font-mono text-[10px] tracking-[0.15em] uppercase text-mist mt-0.5">
          ${addon.price} {addon.unit}{addon.desc ? ` · ${addon.desc}` : ""}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button data-testid={`addon-dec-${addon.key}`} onClick={() => onSet(addon.key, Math.max(0, qty - 1))} className="rounded-lg border border-white/15 p-2 text-mist hover:text-crystal hover:border-crystal transition-colors duration-200" aria-label={`Remove ${addon.label}`}>
          <Minus size={13} />
        </button>
        <span className="w-8 text-center font-mono text-sm text-white" data-testid={`addon-count-${addon.key}`}>{qty}</span>
        <button data-testid={`addon-inc-${addon.key}`} onClick={() => onSet(addon.key, Math.min(200, qty + 1))} className="rounded-lg border border-white/15 p-2 text-mist hover:text-crystal hover:border-crystal transition-colors duration-200" aria-label={`Add ${addon.label}`}>
          <Plus size={13} />
        </button>
      </div>
    </div>
  );
}

export default function BookingModal({ open, onClose, preset }) {
  const [step, setStep] = useState(0);
  const [serviceKey, setServiceKey] = useState("res_2story");
  const [scope, setScope] = useState("inside_out");
  const [unit, setUnit] = useState("pane");
  const [count, setCount] = useState(12);
  const [frequency, setFrequency] = useState("one_time");
  const [planMonths, setPlanMonths] = useState(0);
  const [addons, setAddons] = useState(EMPTY_ADDONS);
  const [showAddons, setShowAddons] = useState(false);
  const [date, setDate] = useState(null);
  const [slot, setSlot] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", email: "", address: "", notes: "" });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [booking, setBooking] = useState(null);

  const service = RATES[serviceKey];
  const addonsTotal = calcAddons(addons);
  const glassTotal = calcTotal(serviceKey, unit, scope, count);
  const total = glassTotal + addonsTotal;
  const isCommercial = service.kind === "commercial";
  const effectiveScope = isCommercial ? "inside_out" : scope;
  const minimumApplied = isCommercial && glassTotal === service.minimum && count * service[unit].inside_out < service.minimum;
  const extras = addonCount(addons);
  const isPlan = frequency !== "one_time" && planMonths > 0;
  const visits = visitsFor(frequency, planMonths);
  const planTotal = total * visits;
  const perMonth = isPlan ? Math.round(planTotal / planMonths) : monthlyEstimate(total, frequency);

  const dates = useMemo(() => {
    const out = [];
    const d = new Date();
    for (let i = 0; i < 14; i++) {
      const dt = new Date(d.getFullYear(), d.getMonth(), d.getDate() + i);
      out.push({
        value: `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`,
        dow: dt.toLocaleDateString("en-US", { weekday: "short" }),
        day: dt.getDate(),
        month: dt.toLocaleDateString("en-US", { month: "short" }),
        full: dt.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" }),
      });
    }
    return out;
  }, [open]);

  useEffect(() => {
    if (open && preset?.serviceKey) {
      setServiceKey(preset.serviceKey);
      if (preset.scope) setScope(preset.scope);
      setFrequency("one_time");
      setPlanMonths(0);
      setStep(0);
      setBooking(null);
    }
  }, [open, preset]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const canNext = step === 1 ? !!(date && slot) : true;

  const validateDetails = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Your name is required";
    if (!/^[0-9+()\-.\s]{7,}$/.test(form.phone.trim())) e.phone = "Enter a valid phone number";
    if (!form.address.trim()) e.address = "Service address is required";
    if (form.email && !/^\S+@\S+\.\S+$/.test(form.email)) e.email = "Enter a valid email";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async () => {
    if (!validateDetails()) return;
    setSubmitting(true);
    try {
      const b = await createBooking({
        name: form.name,
        phone: form.phone,
        email: form.email || undefined,
        address: form.address,
        notes: form.notes || undefined,
        service_key: serviceKey,
        scope: effectiveScope,
        unit,
        count,
        frequency,
        plan_months: frequency !== "one_time" ? planMonths : 0,
        addons,
        date,
        time_slot: slot,
      });
      setBooking(b);
      setStep(3);
      toast.success(`Booked for ${b.date} at ${b.time_slot}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || "Could not save your booking — please call us instead.");
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => {
    onClose();
    if (booking) {
      setStep(0); setBooking(null); setDate(null); setSlot(null);
      setForm({ name: "", phone: "", email: "", address: "", notes: "" });
      setCount(12); setUnit("pane"); setScope("inside_out"); setServiceKey("res_2story");
      setFrequency("one_time"); setPlanMonths(0); setAddons(EMPTY_ADDONS); setShowAddons(false);
    }
  };

  const scopeLabel = effectiveScope === "exterior" ? "Exterior only" : "Inside & out";
  const unitLabel = unit === "pane" ? "panes" : "windows";

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="booking-modal"
        >
          <div className="absolute inset-0 bg-midnight/80 backdrop-blur-md" onClick={close} data-testid="booking-modal-backdrop" />
          <motion.div
            initial={{ y: 60, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 40, opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="relative z-10 w-full sm:max-w-2xl max-h-[94vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl border border-white/15 bg-[#0B0E14]"
            style={{ boxShadow: "0 60px 140px -40px rgba(0,0,0,1)" }}
          >
            {/* header */}
            <div className="sticky top-0 z-10 bg-[#0B0E14]/95 backdrop-blur border-b border-white/10 px-6 sm:px-8 pt-6 pb-4">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] uppercase text-mist">
                  {STEPS.map((s, i) => (
                    <span key={s} className="flex items-center gap-2">
                      <span className={i === step ? "text-crystal" : i < step || booking ? "text-white/70" : ""}>
                        {String(i + 1).padStart(2, "0")} {s}
                      </span>
                      {i < STEPS.length - 1 && <span className="text-white/20">/</span>}
                    </span>
                  ))}
                </div>
                <button data-testid="booking-modal-close" onClick={close} className="rounded-full border border-white/15 p-2 text-mist hover:text-white hover:border-crystal transition-colors duration-200" aria-label="Close booking">
                  <X size={16} />
                </button>
              </div>
            </div>

            <div className="px-6 sm:px-8 py-8">
              <AnimatePresence mode="wait">
                {/* STEP 0 — service */}
                {step === 0 && (
                  <motion.div key="s0" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.3 }}>
                    <h3 className="font-display text-3xl text-white mb-2">What are we cleaning?</h3>
                    <p className="text-mist text-sm mb-7">Pick your glass. Your exact total updates live.</p>

                    <div className="grid sm:grid-cols-3 gap-3 mb-8">
                      {Object.values(RATES).map((r) => (
                        <button
                          key={r.key}
                          data-testid={`service-option-${r.key}`}
                          onClick={() => { setServiceKey(r.key); setFrequency("one_time"); setPlanMonths(0); }}
                          className={`rounded-2xl border p-5 text-left transition-all duration-200 ${
                            serviceKey === r.key ? "border-crystal bg-crystal/10" : "border-white/15 bg-ink/60 hover:border-white/35"
                          }`}
                        >
                          {r.kind === "residential" ? <Home size={18} className="text-crystal mb-3" /> : <Building2 size={18} className="text-crystal mb-3" />}
                          <div className="font-display text-xl text-white leading-tight">{r.label}</div>
                          <div className="font-mono text-[10px] tracking-[0.15em] uppercase text-mist mt-2">
                            {r.kind === "commercial" ? "Inside & out" : "From $" + r.pane.exterior + " /pane"}
                          </div>
                        </button>
                      ))}
                    </div>

                    {!isCommercial && (
                      <div className="mb-7">
                        <div className="font-mono text-[11px] tracking-[0.25em] uppercase text-mist mb-3">Scope</div>
                        <div className="grid grid-cols-2 gap-3">
                          {[["exterior", "Exterior only"], ["inside_out", "Inside & out"]].map(([val, label]) => (
                            <button
                              key={val}
                              data-testid={`scope-option-${val}`}
                              onClick={() => setScope(val)}
                              className={`rounded-xl border py-3.5 font-mono text-[11px] tracking-[0.2em] uppercase transition-colors duration-200 ${
                                scope === val ? "border-crystal bg-crystal/10 text-crystal" : "border-white/15 text-mist hover:text-white"
                              }`}
                            >
                              {label}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="mb-7">
                      <div className="font-mono text-[11px] tracking-[0.25em] uppercase text-mist mb-3">Count by</div>
                      <div className="grid grid-cols-2 gap-3">
                        {[["pane", "Panes"], ["window", "Windows"]].map(([val, label]) => (
                          <button
                            key={val}
                            data-testid={`unit-option-${val}`}
                            onClick={() => setUnit(val)}
                            className={`rounded-xl border py-3.5 font-mono text-[11px] tracking-[0.2em] uppercase transition-colors duration-200 ${
                              unit === val ? "border-crystal bg-crystal/10 text-crystal" : "border-white/15 text-mist hover:text-white"
                            }`}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="mb-7">
                      <div className="font-mono text-[11px] tracking-[0.25em] uppercase text-mist mb-3">How many {unitLabel}?</div>
                      <div className="flex items-center gap-4">
                        <button data-testid="count-decrease" onClick={() => setCount((c) => Math.max(1, c - 1))} className="rounded-xl border border-white/15 p-3.5 text-mist hover:text-crystal hover:border-crystal transition-colors duration-200" aria-label="Decrease count"><Minus size={16} /></button>
                        <input
                          data-testid="count-input"
                          type="number"
                          min="1"
                          max="500"
                          value={count}
                          onChange={(e) => setCount(Math.min(500, Math.max(1, Number(e.target.value) || 1)))}
                          className="flex-1 text-center rounded-xl bg-ink/80 border border-white/15 px-4 py-3.5 font-mono text-2xl text-white outline-none focus:border-crystal transition-colors duration-200"
                        />
                        <button data-testid="count-increase" onClick={() => setCount((c) => Math.min(500, c + 1))} className="rounded-xl border border-white/15 p-3.5 text-mist hover:text-crystal hover:border-crystal transition-colors duration-200" aria-label="Increase count"><Plus size={16} /></button>
                      </div>
                    </div>

                    {/* Schedule — one-time or a recurring plan */}
                    <div className="mb-7">
                      <div className="font-mono text-[11px] tracking-[0.25em] uppercase text-mist mb-3">Schedule</div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        {FREQUENCIES.map((f) => (
                          <button
                            key={f.key}
                            data-testid={`frequency-option-${f.key}`}
                            onClick={() => { setFrequency(f.key); if (f.key === "one_time") setPlanMonths(0); else setPlanMonths((m) => (m > 0 ? m : 3)); }}
                            className={`rounded-xl border py-3.5 font-mono text-[11px] tracking-[0.15em] uppercase transition-colors duration-200 ${
                              frequency === f.key ? "border-crystal bg-crystal/10 text-crystal" : "border-white/15 text-mist hover:text-white"
                            }`}
                          >
                            {f.label}
                          </button>
                        ))}
                      </div>
                      {frequency !== "one_time" && (
                        <div className="mt-4">
                          <div className="font-mono text-[11px] tracking-[0.25em] uppercase text-mist mb-3">Plan length</div>
                          <div className="grid grid-cols-3 gap-3">
                            {PLAN_LENGTHS.map((p) => (
                              <button
                                key={p.months}
                                data-testid={`plan-${p.months}-btn`}
                                onClick={() => setPlanMonths(p.months)}
                                className={`rounded-xl border py-3.5 font-mono text-[11px] tracking-[0.15em] uppercase transition-colors duration-200 ${
                                  planMonths === p.months ? "border-crystal bg-crystal/10 text-crystal" : "border-white/15 text-mist hover:text-white"
                                }`}
                              >
                                {p.label}
                              </button>
                            ))}
                          </div>
                          <p className="mt-3 text-xs text-mist" data-testid="plan-hint">
                            {planLabel(frequency, planMonths)} — <span className="text-crystal">${total}</span> per visit,
                            ≈ <span className="text-crystal">${perMonth}</span>/month,{" "}
                            <span className="text-crystal">${planTotal}</span> over the whole plan. Same price as the website — locked in.
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Around the glass — optional add-ons */}
                    <div className="mb-7">
                      <button
                        data-testid="addons-toggle"
                        onClick={() => setShowAddons((s) => !s)}
                        className="w-full flex items-center justify-between rounded-xl border border-white/15 bg-ink/50 px-4 py-3.5 text-left transition-colors duration-200 hover:border-white/30"
                      >
                        <span className="flex items-center gap-3">
                          <span className="font-mono text-[11px] tracking-[0.25em] uppercase text-mist">Around the glass</span>
                          <span className={`font-mono text-[10px] tracking-[0.15em] uppercase ${extras > 0 ? "text-crystal" : "text-mist/60"}`}>
                            {extras > 0 ? `${extras} extras · +$${addonsTotal}/visit` : "trims, ledges, screens… (optional)"}
                          </span>
                        </span>
                        <ChevronDown size={16} className={`text-mist transition-transform duration-200 ${showAddons ? "rotate-180" : ""}`} />
                      </button>
                      {showAddons && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          className="mt-3 space-y-2 overflow-hidden"
                          data-testid="addons-list"
                        >
                          {ADDONS.map((a) => (
                            <AddonRow key={a.key} addon={a} qty={addons[a.key]} onSet={(k, v) => setAddons((s) => ({ ...s, [k]: v }))} />
                          ))}
                          <p className="font-mono text-[10px] tracking-[0.15em] uppercase text-mist/60 pt-1">
                            Everything around the glass, priced flat — no on-site surprises.
                          </p>
                        </motion.div>
                      )}
                    </div>

                    <div className="mb-8">
                      <div className="rounded-2xl border border-crystal/40 bg-crystal/[0.07] px-6 py-5" data-testid="live-total">
                        <div className="flex items-center justify-between gap-4">
                          <div>
                            <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-mist">Your total{isPlan ? " per visit" : ""}</div>
                            <div className="text-mist text-xs mt-1">
                              {service.label} · {scopeLabel} · {count} {unitLabel}
                              {extras > 0 ? ` · ${extras} extras` : ""}
                              {isPlan ? ` · ${frequencyLabel(frequency)} for ${planMonths} months` : ""}
                              {minimumApplied ? " · $75 visit minimum applied" : ""}
                            </div>
                            {extras > 0 && (
                              <div className="text-mist/70 text-[11px] mt-0.5" data-testid="addons-breakdown">
                                Glass ${glassTotal} + around the glass ${addonsTotal}
                              </div>
                            )}
                          </div>
                          <div className="font-display text-5xl text-crystal" data-testid="live-total-amount">${total}</div>
                        </div>
                        {isPlan && (
                          <div className="mt-3 pt-3 border-t border-crystal/20 font-mono text-[10px] tracking-[0.15em] uppercase text-mist" data-testid="plan-total">
                            {visits} visits · ≈ ${perMonth}/month · plan total ${planTotal}
                          </div>
                        )}
                      </div>
                    </div>

                    <button data-testid="booking-next-0" onClick={() => setStep(1)} className="w-full flex items-center justify-center gap-2 rounded-full bg-crystal py-4 font-mono text-[11px] font-semibold tracking-[0.25em] uppercase text-midnight transition-transform duration-200 hover:-translate-y-0.5">
                      Set a time <ArrowRight size={14} />
                    </button>
                  </motion.div>
                )}

                {/* STEP 1 — date & time */}
                {step === 1 && (
                  <motion.div key="s1" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.3 }}>
                    <h3 className="font-display text-3xl text-white mb-2">When should I come?</h3>
                    <p className="text-mist text-sm mb-7">
                      Pick a day and an arrival window — confirmed by text.
                      {isPlan ? ` ${frequencyLabel(frequency).toLowerCase()} visits for ${planMonths} months start from this first one.` : ""}
                    </p>

                    <div className="grid grid-cols-7 gap-2 mb-8" data-testid="date-strip">
                      {dates.map((d) => (
                        <button
                          key={d.value}
                          data-testid={`date-option-${d.value}`}
                          onClick={() => setDate(d.value)}
                          className={`rounded-xl border py-3 flex flex-col items-center gap-0.5 transition-colors duration-200 ${
                            date === d.value ? "border-crystal bg-crystal/10 text-crystal" : "border-white/15 text-mist hover:text-white hover:border-white/35"
                          }`}
                        >
                          <span className="font-mono text-[9px] tracking-[0.15em] uppercase">{d.dow}</span>
                          <span className="font-display text-xl leading-none">{d.day}</span>
                          <span className="font-mono text-[9px] tracking-[0.15em] uppercase opacity-70">{d.month}</span>
                        </button>
                      ))}
                    </div>

                    <div className="font-mono text-[11px] tracking-[0.25em] uppercase text-mist mb-3">Arrival window</div>
                    <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mb-8" data-testid="slot-grid">
                      {TIME_SLOTS.map((t) => (
                        <button
                          key={t}
                          data-testid={`time-slot-${t.replace(/[\s:]/g, "").toLowerCase()}`}
                          onClick={() => setSlot(t)}
                          className={`rounded-xl border py-3 font-mono text-[11px] tracking-[0.1em] transition-colors duration-200 ${
                            slot === t ? "border-crystal bg-crystal/10 text-crystal" : "border-white/15 text-mist hover:text-white hover:border-white/35"
                          }`}
                        >
                          {t}
                        </button>
                      ))}
                    </div>

                    <div className="flex gap-3">
                      <button data-testid="booking-back-1" onClick={() => setStep(0)} className="flex items-center gap-2 rounded-full border border-white/20 px-6 py-4 font-mono text-[11px] tracking-[0.2em] uppercase text-mist hover:text-white transition-colors duration-200">
                        <ArrowLeft size={14} /> Back
                      </button>
                      <button
                        data-testid="booking-next-1"
                        disabled={!canNext}
                        onClick={() => setStep(2)}
                        className={`flex-1 flex items-center justify-center gap-2 rounded-full py-4 font-mono text-[11px] font-semibold tracking-[0.25em] uppercase transition-all duration-200 ${
                          canNext ? "bg-crystal text-midnight hover:-translate-y-0.5" : "bg-white/10 text-mist cursor-not-allowed"
                        }`}
                      >
                        Your details <ArrowRight size={14} />
                      </button>
                    </div>
                  </motion.div>
                )}

                {/* STEP 2 — details */}
                {step === 2 && (
                  <motion.div key="s2" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.3 }}>
                    <h3 className="font-display text-3xl text-white mb-2">Where do I come?</h3>
                    <p className="text-mist text-sm mb-7">
                      {service.label} · {scopeLabel} · {count} {unitLabel}
                      {extras > 0 ? ` · ${extras} extras (+$${addonsTotal}/visit)` : ""}
                      {isPlan ? ` · ${planLabel(frequency, planMonths)}` : ""} ·{" "}
                      <span className="text-crystal">{dates.find((d) => d.value === date)?.full} · {slot}</span>
                    </p>

                    <div className="space-y-4">
                      <div>
                        <input data-testid="booking-name-input" placeholder="Your name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
                        {errors.name && <p className="mt-1.5 text-xs text-red-400" data-testid="error-name">{errors.name}</p>}
                      </div>
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <input data-testid="booking-phone-input" placeholder="Phone *" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} />
                          {errors.phone && <p className="mt-1.5 text-xs text-red-400" data-testid="error-phone">{errors.phone}</p>}
                        </div>
                        <div>
                          <input data-testid="booking-email-input" placeholder="Email (optional)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
                          {errors.email && <p className="mt-1.5 text-xs text-red-400" data-testid="error-email">{errors.email}</p>}
                        </div>
                      </div>
                      <div>
                        <input data-testid="booking-address-input" placeholder="Service address in Naperville * " value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className={inputCls} />
                        {errors.address && <p className="mt-1.5 text-xs text-red-400" data-testid="error-address">{errors.address}</p>}
                      </div>
                      <textarea data-testid="booking-notes-input" placeholder="Notes — gate codes, dogs, tricky panes…" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className={`${inputCls} resize-none`} />
                    </div>

                    <div className="flex gap-3 mt-8">
                      <button data-testid="booking-back-2" onClick={() => setStep(1)} className="flex items-center gap-2 rounded-full border border-white/20 px-6 py-4 font-mono text-[11px] tracking-[0.2em] uppercase text-mist hover:text-white transition-colors duration-200">
                        <ArrowLeft size={14} /> Back
                      </button>
                      <button
                        data-testid="booking-submit-btn"
                        disabled={submitting}
                        onClick={submit}
                        className={`flex-1 flex items-center justify-center gap-2 rounded-full py-4 font-mono text-[11px] font-semibold tracking-[0.25em] uppercase transition-all duration-200 ${
                          submitting ? "bg-white/10 text-mist cursor-wait" : "bg-crystal text-midnight hover:-translate-y-0.5"
                        }`}
                      >
                        {submitting ? "Locking your slot…" : <>Confirm — pay ${total} with Zelle <ArrowRight size={14} /></>}
                      </button>
                    </div>
                  </motion.div>
                )}

                {/* STEP 3 — Zelle payment */}
                {step === 3 && booking && (
                  <motion.div key="s3" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.3 }}>
                    <div className="flex items-center gap-3 mb-6">
                      <span className="rounded-full bg-crystal/15 border border-crystal/40 p-2.5">
                        <CalendarCheck2 size={20} className="text-crystal" />
                      </span>
                      <div>
                        <h3 className="font-display text-3xl text-white" data-testid="booking-confirmed-title">You're on the schedule.</h3>
                        <p className="text-mist text-sm mt-0.5" data-testid="booking-confirmed-when">
                          {dates.find((d) => d.value === booking.date)?.full || booking.date} · {booking.time_slot} · ref <span className="text-crystal font-mono">{booking.ref}</span>
                        </p>
                      </div>
                    </div>

                    {(booking.addons_total || 0) > 0 && (
                      <p className="mb-5 rounded-xl border border-white/15 bg-ink/60 px-5 py-3.5 text-sm text-mist" data-testid="extras-note">
                        <span className="text-crystal font-mono text-[10px] tracking-[0.2em] uppercase block mb-1">Around the glass — +${booking.addons_total}/visit</span>
                        {ADDONS.filter((a) => (booking.addons?.[a.key] || 0) > 0).map((a) => `${booking.addons[a.key]}× ${a.label.toLowerCase()}`).join(" · ")}
                      </p>
                    )}

                    {booking.frequency !== "one_time" && (
                      <p className="mb-5 rounded-xl border border-crystal/30 bg-crystal/[0.06] px-5 py-3.5 text-sm text-mist" data-testid="recurring-note">
                        <span className="text-crystal font-mono text-[10px] tracking-[0.2em] uppercase block mb-1">{planLabel(booking.frequency, booking.plan_months)}</span>
                        Standing total of <span className="text-white">${booking.total}</span> per visit —{" "}
                        <span className="text-white">${booking.total * visitsFor(booking.frequency, booking.plan_months)}</span> over the whole plan.
                        Same Zelle, same arrival window, every visit.
                      </p>
                    )}

                    <div className="rounded-2xl border border-crystal/40 overflow-hidden" style={{ boxShadow: "0 0 80px -30px rgba(0,229,255,0.4)" }} data-testid="zelle-payment-card">
                      <div className="flex items-center justify-between bg-crystal/10 border-b border-crystal/25 px-6 py-4">
                        <span className="flex items-center gap-2 font-mono text-[11px] tracking-[0.3em] uppercase text-crystal">
                          <Zap size={14} /> Pay {BUSINESS.name} with Zelle
                        </span>
                        <span className="font-display text-4xl text-white" data-testid="zelle-amount">${booking.total}</span>
                      </div>
                      <div className="p-6 space-y-3 bg-ink/60">
                        <CopyRow label="Zelle — phone" value={BUSINESS.zellePhone} testid="zelle-phone" />
                        <CopyRow label="Zelle — email" value={BUSINESS.zelleEmail} testid="zelle-email" />
                        <CopyRow label="Memo / reference" value={booking.ref} testid="zelle-memo" />
                        <ol className="pt-2 space-y-2 text-sm text-mist list-decimal list-inside">
                          <li>Open your banking app and tap Zelle.</li>
                          <li>Send <span className="text-white">${booking.total}</span> to the handle above{isPlan ? " after each visit" : ""}.</li>
                          <li>Use the reference as the memo so I can match it to your booking.</li>
                        </ol>
                      </div>
                    </div>

                    <p className="mt-5 text-mist text-xs leading-relaxed">
                      Your slot is locked. Payment by the day of service keeps it
                      confirmed — questions? Call{" "}
                      <a href={BUSINESS.phoneHref} className="text-crystal" data-testid="zelle-call-link">{BUSINESS.phone}</a>.
                    </p>

                    <a
                      href={BUSINESS.reviewUrl}
                      target="_blank"
                      rel="noreferrer"
                      data-testid="confirmation-review-link"
                      className="mt-5 flex items-center justify-center gap-2 w-full rounded-full border border-white/20 py-3.5 font-mono text-[10px] tracking-[0.2em] uppercase text-mist hover:text-crystal hover:border-crystal transition-colors duration-200"
                    >
                      <Star size={12} /> Had us before? Leave a Google review
                    </a>

                    <button data-testid="booking-done-btn" onClick={close} className="mt-4 w-full rounded-full bg-crystal py-4 font-mono text-[11px] font-semibold tracking-[0.25em] uppercase text-midnight transition-transform duration-200 hover:-translate-y-0.5">
                      Done
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
